import { z } from 'zod';
import type { Category } from '@/models';
import { getPublicSupabaseClient } from './supabase-client';

export interface CategoryRepository {
  list(): Promise<Category[]>;
  findBySlug(slug: string): Promise<Category | null>;
}

const rowSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  icon: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
  display_order: z.number().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
});

function toCategory(row: z.infer<typeof rowSchema>): Category {
  return {
    id: row.id,
    slug: row.slug,
    nameAr: row.name_ar,
    nameEn: row.name_en,
    icon: row.icon ?? null,
    imageUrl: row.image_url ?? null,
    displayOrder: row.display_order ?? undefined,
    isActive: row.is_active ?? undefined,
  };
}

const CATEGORY_COLUMNS = 'id, slug, name_ar, name_en, icon, image_url, display_order, is_active';

export const supabaseCategoryRepository: CategoryRepository = {
  async list() {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }

    const { data, error } = await supabase
      .from('categories')
      .select(CATEGORY_COLUMNS)
      .order('display_order', { ascending: true });

    if (error || !data) {
      return [];
    }
    return data.flatMap((row) => {
      const parsed = rowSchema.safeParse(row);
      return parsed.success ? [toCategory(parsed.data)] : [];
    });
  },

  async findBySlug(slug) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('categories')
      .select(CATEGORY_COLUMNS)
      .eq('slug', slug)
      .maybeSingle();

    if (error || !data) {
      return null;
    }
    const parsed = rowSchema.safeParse(data);
    return parsed.success ? toCategory(parsed.data) : null;
  },
};
